import { useCallback, useEffect, useState } from "react";
import { redirectIfPhonePe } from "../lib/payRail";
import { PayRailButtons } from "../components/payments/PayRailButtons";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Landmark, ShieldCheck, Loader2, Sparkles, CheckCircle2, Lock,
  AlertTriangle, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function VCBInduction() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Guard: if module is off, the invitation endpoint returns 404
      // anyway, but check module state explicitly so we can render a
      // gentle "not yet activated" message instead of a generic 404.
      try {
        const mod = await axios.get(`${API}/vcb/module-status`,
                                       { headers: authHeaders() });
        if (mod.data && mod.data.enabled === false) {
          setError("Vinci Core Banking is not yet activated. Please wait for your Vinci account manager.");
          return;
        }
      } catch (e) {
        if (e.response?.status === 404) {
          setError("Vinci Core Banking is not yet activated. Please wait for your Vinci account manager.");
          return;
        }
      }
      const r = await axios.get(`${API}/vcb/invitation/${token}`);
      setInvitation(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Invitation not found or expired");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const isLoggedIn = () =>
    !!(localStorage.getItem("token") || localStorage.getItem("access_token"));

  const pay = async (railChoice = "razorpay") => {
    if (!isLoggedIn()) {
      toast.info("Log in as the invited email first, then return to this page.");
      navigate(`/login?redirect=/vcb/induct/${token}`);
      return;
    }
    setCheckingOut(railChoice);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Razorpay could not be loaded");
      const co = await axios.post(`${API}/vcb/induction/checkout?rail=${railChoice}`,
                                     { token },
                                     { headers: authHeaders() });
      if (redirectIfPhonePe(co.data)) return;
      const { order_id, amount, currency, key_id } = co.data;
      if (!key_id) throw new Error("Razorpay is not configured on the server");
      const options = {
        key: key_id,
        amount: Math.round(amount * 100),
        currency,
        name: "Vinci Inc.",
        description: "Vinci Core Banking · Mapping Fee",
        order_id,
        theme: { color: "#D8B23E" },
        handler: async (resp) => {
          try {
            const v = await axios.post(`${API}/vcb/induction/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              token,
            }, { headers: authHeaders() });
            if (v.data?.ok) {
              setSuccess(true);
              toast.success("Inducted into Vinci Core Banking");
            } else {
              toast.error("Payment verification failed");
            }
          } catch (e) {
            toast.error(e.response?.data?.detail || "Verification failed");
          }
        },
        modal: { ondismiss: () => setCheckingOut(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not start payment");
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#F5F5F0] grid place-items-center">
        <Loader2 size={22} className="animate-spin text-[#D8B23E]" />
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#F5F5F0] grid place-items-center px-4">
        <div className="max-w-md text-center">
          <AlertTriangle size={28} className="mx-auto text-[#E5A93E] mb-3" />
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#E5A93E]">
            Invitation Unavailable
          </div>
          <h1 className="font-serif text-3xl mt-2">{error || "Not found"}</h1>
          <p className="font-body text-sm text-[#C9C9C2] mt-3">
            If you believe this is a mistake, please contact your Vinci account manager
            for a fresh invitation.
          </p>
        </div>
      </div>
    );
  }

  if (success || invitation.status === "paid") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#050505] via-[#0A0906] to-[#050505] text-[#F5F5F0] grid place-items-center px-4">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                     className="max-w-lg text-center space-y-4">
          <CheckCircle2 size={48} className="mx-auto text-[#2FB999]" />
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#2FB999]">
            Induction Complete
          </div>
          <h1 className="font-serif text-4xl">Welcome to Vinci Core Banking</h1>
          <p className="font-body text-[#C9C9C2] leading-relaxed">
            Your induction is on record. You now hold banking-grade privileges across
            the Vinci network — zero handling charges on wallet-to-wallet trades and
            permanent client mapping capacity.
          </p>
          <Button data-testid="vcb-induction-goto-dashboard"
                   onClick={() => navigate("/dashboard")}
                   className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.28em] text-[10px] hover:bg-[#E5C450] mt-2">
            Enter Vinci Console <ArrowRight size={11} className="ml-1.5" />
          </Button>
        </motion.div>
      </div>
    );
  }

  const revoked = invitation.status === "revoked";
  const expired = invitation.status === "expired";
  const canPay = !revoked && !expired;

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F0] px-4 py-16 md:py-24">
      <div className="max-w-3xl mx-auto space-y-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">
            Vinci Inc. · Sovereign Ledger
          </div>
          <h1 className="font-serif text-4xl md:text-5xl mt-2 leading-tight">
            Executive Banking Invitation
          </h1>
          <div className="font-body text-sm text-[#C9C9C2] mt-3">
            Issued to <span className="text-[#D8B23E]">{invitation.email}</span>
            {" · "}Valid until {invitation.expires_at?.slice(0, 10)}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                     transition={{ delay: 0.1 }}
                     className="border border-[#D8B23E]/40 rounded-sm bg-gradient-to-br from-[#0A0906] via-[#12100A] to-[#0A0906] p-8 md:p-10 space-y-6">
          <div className="flex items-start gap-4">
            <Landmark size={28} className="text-[#D8B23E] mt-1 shrink-0" />
            <div>
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#D8B23E]">
                Vinci Core Banking · Mapping Fee
              </div>
              <div className="font-serif text-5xl mt-1">
                ₹{Number(invitation.fee_amount).toLocaleString("en-IN")}
              </div>
              <div className="text-[11px] text-[#8A8A82] font-body italic mt-1">
                One-time payment. Lifetime privilege.
              </div>
            </div>
          </div>

          {invitation.note && (
            <div className="border-l-2 border-[#D8B23E] bg-black/40 px-4 py-3 font-body text-sm italic text-[#C9C9C2]">
              {invitation.note}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-3 text-[13px] font-body text-[#C9C9C2] leading-relaxed">
            <Feature icon={ShieldCheck} title="Zero-Fee Transfers"
                      blurb="0% handling charges on every wallet-to-wallet trade inside the Vinci ecosystem." />
            <Feature icon={Sparkles} title="Unlimited Pulse"
                      blurb="No transaction ceilings on intra-ecosystem transfers, subject to your bank capacity." />
            <Feature icon={Landmark} title="Client Mapping Engine"
                      blurb="Map your downstream customers once — trade directly forever." />
            <Feature icon={Lock} title="Sovereign Vault"
                      blurb="Bank-grade audit trail, executive-only visibility, admin-issued treasury keys." />
          </div>

          {canPay ? (
            <div data-testid="vcb-induction-pay-btn">
              <PayRailButtons testId="vcb-induction-pay" onPay={pay} busy={checkingOut}
                              amountLabel={`₹${Number(invitation.fee_amount).toLocaleString("en-IN")}`}
                              note="Banking unlocks the moment either rail confirms your payment." />
            </div>
          ) : (
            <div className="border border-[#E5484D]/40 rounded-sm p-4 text-center">
              <AlertTriangle size={16} className="mx-auto text-[#E5484D] mb-1" />
              <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#E5484D]">
                {revoked ? "Invitation Revoked" : "Invitation Expired"}
              </div>
              <div className="text-[11px] text-[#8A8A82] font-body italic mt-1">
                Please request a fresh invitation from your Vinci account manager.
              </div>
            </div>
          )}
        </motion.div>

        <div className="text-[10px] text-[#8A8A82] font-body text-center">
          Payments are processed by Razorpay · Secured under Vinci's Sovereign Ledger protocol.
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, blurb }) {
  return (
    <div className="flex items-start gap-2.5 border-t border-white/5 pt-3">
      <Icon size={14} className="text-[#D8B23E] mt-0.5 shrink-0" />
      <div>
        <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#F5F5F0]">{title}</div>
        <div className="text-[11px] text-[#8A8A82] mt-0.5">{blurb}</div>
      </div>
    </div>
  );
}
